import React, {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import {isEqual} from 'lodash';
import moment from 'moment';
import {usePartnersUIContext} from '../PartnersUIContext';
import FiltersBar from '../../../_common/components/FiltersBar';
import ButtonExportToExcel from '../../../_common/components/ButtonExportToExcel';
import {FilterFromDate, FilterToDate} from '../../../_common/components/FilterDateRange';
import FilterSelect from '../../../_common/components/FilterSelect';
import PopupManagePartners from '../PopupManagePartners';
import * as actions from '../../_redux/partnersActions';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {generateExcelFile} from '../../../_common/utils/excel';
import {getLastCompletedStepLabel} from '../../../_common/utils/getLastCompletedStepLabel';
import {headersPartners} from '../../../_common/constants/exportedFilesHeaders';
import {partnersHeader} from '../../../_common/constants/exportedFilesMainHeaders';
import {RequestCancellationManager} from '../../../_common/utils/RequestCancellationManager';

const prepareFilter = (queryParams, values) => {
  const {fromDate, toDate, affiliateCompanyID} = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.fromDate;
  }
  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).add(1, 'days').format('YYYY-MM-DD');
  } else {
    delete newQueryParams.toDate;
  }
  if (Boolean(affiliateCompanyID)) {
    filter.affiliateCompanyID = affiliateCompanyID;
  } else {
    delete newQueryParams.affiliateCompanyID;
  }
  return {...newQueryParams, ...filter};
};

export function PartnersFilter() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const partnersUIContext = usePartnersUIContext();
  const partnersUIProps = useMemo(() => {
    return {
      queryParams: partnersUIContext.queryParams,
      setQueryParams: partnersUIContext.setQueryParams,
    };
  }, [partnersUIContext]);
  const [loading, setLoading] = useState(false);

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(actions.getPartnersListIDPrefix);

    const newQueryParams = prepareFilter(partnersUIProps.queryParams, values);
    if (!isEqual(newQueryParams, partnersUIProps.queryParams)) {
      partnersUIProps.setQueryParams(newQueryParams);
    }
  };

  useEffect(() => {
    dispatch(actions.getPartnersOptions());
  }, []);
  const options = useSelector((state) => state.partners.partnersOptions, shallowEqual);
  const mappedOptions = options.filter(Boolean).map(item => {
    const {name, uniqueId} = item;
    if (name && uniqueId) {
      return {label: name, value: uniqueId};
    }
  });

  const qs = new URLSearchParams(window.location.search);
  const fromDate = qs.get('fromDate') || '';
  const toDate = Boolean(qs.get('toDate'))
    ? moment(qs.get('toDate')).subtract(1, 'days').toDate()
    : '';
  const affiliateCompanyID = qs.get('affiliateCompanyID') || '';

  const exportPartnersFileFunc = () => {
    setLoading(true);
    dispatch(actions.exportPartnersFileAction(partnersUIProps.queryParams))
      .then(prepareDataForExcel)
      .then((data) => {
        generateExcelFile(data, 'Partners.xlsx', partnersHeader);
      })
      .catch((error) => {
        console.error(`Error: + ${error}`);
        setLoading(false);
      })
      .finally(() => setLoading(false));
  };

  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        profile,
        email,
        createdAt,
        partnerName,
        lastCompletedStep
      } = item;

      const fullName = `${profile.firstName || ''} ${profile.lastName || ''}`;

      return {
        [headersPartners[0]]: index + 1,
        [headersPartners[1]]: fullName,
        [headersPartners[2]]: profile.idNumber || '',
        [headersPartners[3]]: profile.phone,
        [headersPartners[4]]: email,
        [headersPartners[5]]: moment(createdAt).format('DD.MM.YYYY'),
        [headersPartners[6]]: getLastCompletedStepLabel(lastCompletedStep),
        [headersPartners[7]]: partnerName,
      };
    });
  };

  const containerClassnames = 'w-200px position-relative my-1 px-3';

  return (
    <Formik
      initialValues={{fromDate, toDate, affiliateCompanyID}}
      onSubmit={(values) => applyFilter(values)}
      enableReinitialize={true}
    >
      {({values, ...formikProps}) => (
        <FiltersBar
          filters={
            <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
              <FilterFromDate
                values={values}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterToDate
                values={values}
                containerStyles={containerClassnames}
                {...formikProps}
              />
              <FilterSelect
                fieldName={'affiliateCompanyID'}
                valueName={values.affiliateCompanyID}
                options={mappedOptions}
                placeholder={intl.formatMessage({id: 'partners-filter-partners', defaultMessage: 'Partners'})}
                containerStyles={containerClassnames}
                {...formikProps}
              />
            </div>
          }
          buttons={
            <div className="d-flex align-items-center justify-content-between">
              <ButtonExportToExcel
                loading={loading}
                exportFunc={exportPartnersFileFunc}
              />
              <PopupManagePartners/>
            </div>
          }
        />
      )}
    </Formik>
  );
}